import { useRef, useCallback, useState } from 'react';
import { retrievePostcodeInfo } from '../../../../../insfrastructure/services/cep';
import { validateCEP } from '../../../../../insfrastructure/utils/form-validators';
import { CEP_DEBOUNCE_MS } from '../constants';
import { CompanyProfile } from '../types';

export const useCEP = (
  companyProfile: CompanyProfile,
  onCompanyProfileUpdate: (updates: Partial<CompanyProfile>) => void,
  onCepError?: (message: string) => void,
  onCepSuccess?: () => void
) => {
  const cepTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [loadingCEP, setLoadingCEP] = useState(false);

  const handleCEPChange = useCallback((cepValue: string): void => {
    if (cepTimeoutRef.current) {
      clearTimeout(cepTimeoutRef.current);
    }

    cepTimeoutRef.current = setTimeout(async () => {
      const cepLimpo = cepValue.replace(/\D/g, '');
      
      if (cepLimpo.length < 8) {
        onCompanyProfileUpdate({
          address: '',
          address_district: '',
          city_name: '',
          uf: '',
          city_id: ''
        });
        setLoadingCEP(false);
        return;
      }

      if (validateCEP(cepValue) !== '') {
        setLoadingCEP(false);
        return;
      }

      // Limpar endereço anterior antes de consultar o novo CEP (evita concatenar com o antigo)
      onCompanyProfileUpdate({
        address: '',
        address_district: '',
        city_name: '',
        uf: '',
        city_id: ''
      });
      setLoadingCEP(true);
      try {
        const cepData = await retrievePostcodeInfo(cepLimpo);
        
        const addressParts = [];
        if (cepData.logradouro) addressParts.push(cepData.logradouro);
        if (cepData.bairro) addressParts.push(cepData.bairro);
        if (cepData.cidade) addressParts.push(cepData.cidade);
        if (cepData.uf) addressParts.push(cepData.uf);
        const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : '';
        
        onCompanyProfileUpdate({
          address: fullAddress,
          address_district: cepData.bairro || '',
          city_name: cepData.cidade || '',
          uf: cepData.uf || '',
          city_id: cepData.city_id ? String(cepData.city_id) : ''
        });
        onCepSuccess?.();
      } catch (error: unknown) {
        console.error('Erro ao buscar CEP:', error);
        onCepError?.('CEP inválido');
      } finally {
        setLoadingCEP(false);
      }
    }, CEP_DEBOUNCE_MS);
  }, [onCompanyProfileUpdate, onCepError, onCepSuccess]);

  return { handleCEPChange, loadingCEP };
};

